"use client";

import { useState, useEffect, useCallback } from "react";
import {
  NoteSearchCriteria,
  getNoteType,
  getNoteSearchQuery,
} from "./api/index";
import { config } from "../../../../Environment";

import useRetryFetch from "../../../../hooks/useRetryFetch";
import { useTranslation } from "react-i18next";

export function useNotes({
  processId,
  // activeFilter = "all",
  searchQuery = "",
  onError,
  onSuccess,
}: {
  processId?: string;
  // activeFilter?: string;
  searchQuery?: string;
  onError?: (arg0: any) => void;
  onSuccess?: (arg0: any) => void;
}) {
  const [notes, setNotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const retryFetch = useRetryFetch();
  const { t } = useTranslation();

  // Load notes on mount
  useEffect(() => {
    loadNotes();
  }, [processId, searchQuery]);

  const loadNotes = useCallback(async () => {
    if (!processId) {
      setNotes([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const criteria: NoteSearchCriteria = {};
      criteria.processId = processId;
      if (searchQuery) {
        criteria.content = searchQuery;
      }
      // if (activeFilter && activeFilter !== "all") {
      //   criteria.type = getNoteType(activeFilter);
      // }
      const query = getNoteSearchQuery(criteria);
      const response = await retryFetch(
        `${config.API_BASE}/api/note/search?${query}`,
        {
          credentials: "include",
        }
      );

      if (!response.ok) {
        const errorMsg = "Failed to load notes";
        setError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      const data = await response.json();
      setNotes(data.list);
    } catch (error) {
      const errorMsg =
        (error as Error).message || "An unexpected error occurred";
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [processId, onError, searchQuery]);

  const addNote = useCallback(
    async (note: Omit<any, "id" | "createdAt" | "updatedAt">) => {
      const { type, content } = note;

      // Optimistic update with temporary ID
      const tempId = `temp-${Date.now()}`;
      const optimisticNote = {
        _id: tempId,
        processId,
        type,
        format: "text",
        content,
      };
      setNotes((prevNotes) => [...prevNotes, optimisticNote]);

      try {
        const payload = {
          assignedTo: null,
          processId,
          type,
          format: "text",
          content,
        };

        const response = await retryFetch(
          `${config.API_BASE}/api/note/create`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify(payload),
          }
        );

        const data = await response.json();

        if (response.ok) {
          const newNote = {
            _id: data._id,
            processId,
            type,
            format: "text",
            content,
          };
          // Replace optimistic entry with server response
          setNotes((prevNotes) =>
            prevNotes.map((n) => (n._id === tempId ? newNote : n))
          );
          onSuccess?.(t("note_creation_success_message"));
          return newNote;
        } else {
          // Revert optimistic update
          setNotes((prevNotes) => prevNotes.filter((n) => n._id !== tempId));
          const errorMsg = data?.error || "Failed to create note";
          setError(errorMsg);
          onError?.(errorMsg);
          return null;
        }
      } catch (error) {
        // Revert optimistic update
        setNotes((prevNotes) => prevNotes.filter((n) => n._id !== tempId));
        const errorMsg =
          (error as Error).message || "An unexpected error occurred";
        setError(errorMsg);
        onError?.(errorMsg);
        return null;
      }
    },
    [processId, onError, onSuccess]
  );

  const editNote = useCallback(
    async (noteId: string, updates: Partial<any>) => {
      setLoading(true);

      //   const result = await updateNote(noteId, updates);

      const result = await retryFetch(`${config.API_BASE}/api/note/edit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ id: noteId, ...updates }),
      });

      if (result.ok) {
        setNotes((prev) =>
          prev.map((note) =>
            note._id === noteId ? { ...note, ...updates } : note
          )
        );
        onSuccess?.(t("note_update_success_message"));
      } else {
        const errorMsg = "Failed to update note";
        setError(errorMsg);
        onError?.(errorMsg);
      }

      setLoading(false);
      return;
    },
    [onError, onSuccess]
  );

  const removeNote = useCallback(
    async (noteId: string) => {
      setLoading(true);

      //   const result = await deleteNote(noteId);

      const result = await retryFetch(`${config.API_BASE}/api/note/delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ id: noteId }),
      });

      if (result.ok) {
        setNotes((prev) => prev.filter((note) => note._id !== noteId));
        onSuccess?.(t("note_delete_success_message"));
      } else {
        const errorMsg = "Failed to delete note";
        setError(errorMsg);
        onError?.(errorMsg);
      }

      setLoading(false);
      return;
    },
    [onError, onSuccess]
  );

  return {
    notes,
    loading,
    error,
    addNote,
    editNote,
    removeNote,
    refresh: loadNotes,
  };
}
